import { useEffect, useCallback } from 'react';

import { LeadFragment } from '@admin/schema';

import { PhoneWithName, Recipient, MessagesWidgetAccount, MessagesWidgetOrder } from '../types';

export const usePhoneSetup = (
  recipient: Recipient,
  recipientType: 'Account' | 'Lead',
  order: MessagesWidgetOrder | undefined,
  setPhoneList: (phones: PhoneWithName[]) => void,
  setPhoneToSendTo: (phone: PhoneWithName | undefined) => void,
  loadMessages: (page?: number) => void,
) => {
  // Load recipient phones and set up initial state
  const setUpPhones = useCallback(() => {
    if (recipientType === 'Account') {
      const account = recipient as MessagesWidgetAccount;
      const phones: PhoneWithName[] = [];

      // Build phone list from customers
      account.customers.forEach((customer) => {
        customer.phones.forEach((phone) => {
          phones.push({
            ...phone,
            name: customer.name,
          });
        });
      });

      // Check for order contact phone
      if (order?.contact) {
        let orderContactPhone: PhoneWithName | undefined;

        // Check for phone_callable first (from jBuilder), then fall back to phoneObject (from GraphQL)
        if ('phone_callable' in order.contact && (order.contact as any).phone_callable) {
          orderContactPhone = {
            ...(order.contact as any).phone_callable,
            name: `${order.contact.name}, Day of Contact`,
            isOrderContact: true,
          };
        } else if (order.contact.phoneObject) {
          orderContactPhone = {
            ...order.contact.phoneObject,
            name: `${order.contact.name}, Day of Contact`,
            isOrderContact: true,
          };
        }

        if (orderContactPhone) {
          phones.unshift(orderContactPhone);
        }
      }

      // Always set phone list and selection, even if empty
      setPhoneList(phones);
      setPhoneToSendTo(phones[0] || undefined);
    } else if (recipientType === 'Lead') {
      const lead = recipient as LeadFragment;

      if (lead.phone) {
        const phoneWithName: PhoneWithName = {
          ...lead.phone,
          name: lead.name || 'Lead Phone',
        };

        setPhoneToSendTo(phoneWithName);
        setPhoneList([phoneWithName]);
      } else {
        setPhoneToSendTo(undefined);
        setPhoneList([]);
      }
    }

    // Load initial messages
    loadMessages(1);
  }, [recipient, recipientType, order, setPhoneList, setPhoneToSendTo, loadMessages]);

  useEffect(() => {
    setUpPhones();
  }, [setUpPhones]);
};
